import React, {Component, Fragment} from 'react';
import PropTypes from 'prop-types';
import ReactTable from 'react-table';
import axios from 'axios';
import ReactToPrint from 'react-to-print';
import {CSVLink} from 'react-csv';
import {connect} from 'react-redux';
import {compose} from 'recompose';
import {RESOURCES as reportsResources} from '../fuseReportsResourceMap';
import {
    addCommas,
    addFromColumn,
    convertDate,
    convertDateTo24HourString,
    getTodayOffsetHour,
    sortCurrency,
    toCurrency,
} from '../shared/functions';
import {getGetHeaders} from '../shared/serverFunctions';
import {setDefaultTableSort, getTableState, filterCaseInsensitive} from '../shared/tableFunctions';
import CircularProgress from 'material-ui/CircularProgress';
import Dialog from 'material-ui/Dialog';
import DialogSaleReceipt from './DialogSaleReceipt';
import DialogSaleSeq from './DialogSaleSeq';
import FlatButton from 'material-ui/FlatButton';
import RaisedButton from 'material-ui/RaisedButton';
import ExportIcon from 'material-ui/svg-icons/navigation/arrow-downward';
import DetailIcon from 'material-ui/svg-icons/image/remove-red-eye';
import {commonStyles} from '../shared/styles';
import {tableStyles} from '../shared/styles-table';
import Config from '../messages';
import '../shared/react-table.css';

/**
 * Class for retrieving and displaying Recent Transactions Summary in a single table
 *
 */
class RecentTransactionsReport extends Component {
    constructor(props) {
        super(props);

        const offsetHour = getTodayOffsetHour();

        this.state = {
            summaryList: [],
            summaryTableData: [],
            summaryTableHeaders: [
                {label: 'Location ID', key: 'location_id'},
                {label: 'Location Name', key: 'location_name'},
                {label: 'City', key: 'city'},
                {label: 'State', key: 'state'},
                {label: 'DBA', key: 'dba_name'},
                {label: 'Payments', key: 'payments'},
                {label: 'Credits', key: 'credit'},
                {label: 'Net Total', key: 'net'}
            ],
            allowLocationSelection: true,
            detailComplete: false,
            detailList: [],
            detailLoading: false,
            detailTableData: [],
            detailTableHeaders: [
                {label: 'Id', key: 'seq'},
                {label: 'Processed', key: 'sale_date_string'},
                {label: 'Subtotal', key: 'initial_net'},
                {label: 'Tax', key: 'initial_tax'},
                {label: 'Total', key: 'initial_total'},
                {label: 'Register', key: 'register'},
                {label: 'User', key: 'user'},
                {label: 'Mode', key: 'mode'},
                {label: 'Version', key: 'version'},
            ],
            detailLocation: {
                id: null,
                name: null,
                dba_name: null,
                selected: false,
                city: null,
                state: null,
            },
            reportName: 'Recent Transactions',
            summaryComplete: false,
            offsetHour: offsetHour,
            open: false,
            transactionData: null,
            paymentsList: [],
            creditsList: [],
            productList: [],
            productReturnedList: [],
            discountsList: []
        };
        this.detailPosition = null;
        this.handleExportClick = this.handleExportClick.bind(this);
        this.handleSortOrFilterChange = this.handleSortOrFilterChange.bind(this);
    }

    componentDidMount() {
        this.fetchData();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.daysBack !== this.props.daysBack) {
            this.fetchData();
        }
    }

    /* API Calls ------------------------------ */

    /**
     * Get most recent transactions.
     *
     */
    fetchData(locationId) {
        const params = {};
        // Assemble the URI
        let resource;
        if (locationId) {
            params.location_id = locationId;
            params.daysBack = this.props.daysBack;
            resource = reportsResources.RecentTransactionsByLocation.GET_LIST(params);
        } else {
            params.daysBack = this.props.daysBack;
            resource = reportsResources.RecentTransactions.GET_LIST(params);
        }

        axios({
            method: 'get',
            url: resource.uri,
            headers: getGetHeaders()
        }).then((result) => {
            if (result.data && result.data.payload) {
                this.processData(locationId, result.data.payload);
            }
        }).catch((error) => {
            let message = Config.fuse.error.error + this.state.reportName + '.';
            if (error.response) {
                // Server response outside 2xx
            } else if (error.request) {
                // No response
                message = Config.fuse.error.no_response + this.state.reportName + '.';
            } else {
                console.log('Error', error.message);
            }
            this.setState({
                detailLoading: false,
                summaryList: [],
                summaryTableData: [],
                summaryComplete: false,
            }, () => {
                if (typeof this.props.onError === 'function') {
                    this.props.onError(message);
                }
            });
        });
    }

    fetchTransactionData(seq, type) {
        let resource = reportsResources.SaleLogBySeq.GET_LIST(seq);

        axios({
            method: 'get',
            url: resource.uri,
            headers: getGetHeaders()
        }).then((result) => {
            if (result.data && result.data.payload) {
                this.processTransactionData(type, result.data.payload);
            }
        }).catch((error) => {
            if (error.response) {
                // Server response outside 2xx
                console.log(error.response.data);
            } else if (error.request) {
                // No response
                console.log(error.request);
            } else {
                console.log('Error', error.message);
            }
        });
    }

    /* Data Processing ------------------------------ */

    /**
     * Store transaction/type results
     *
     * @param locationId
     * @param data
     */
    processData(locationId, data) {

        const emptyCurrency = toCurrency(0);
        let summaryList = [];
        let detailList = [];
        if (data.aggregations) {
            if (!locationId) {
                if (data.aggregations.by_location && data.aggregations.by_location.buckets) {
                    let results = data.aggregations.by_location.buckets;
                    let resultsTotal = results.length;
                    if (resultsTotal) {
                        // Assemble data for each location key
                        for (let s = 0; s < resultsTotal; s++) {
                            let locationResult = results[s];
                            let credit = locationResult.agg_credit.credit_total.value;
                            let payment = locationResult.agg_payment.payment_total.value;
                            let netTotal = locationResult.net_total.value;
                            let locationSource = locationResult.column_name.hits.hits[0]._source.rxtxMsg;
                            if (locationSource) {
                                let location = locationResult.column_name.hits.hits[0]._source.rxtxMsg.payload.location;
                                // Create a row for each Location
                                const locationRow = {
                                    location_id: (location.id) ? location.id : '----',
                                    location_name: (location.name) ? location.name : '----',
                                    city: (location.city) ? location.city : '(None)',
                                    state: (location.state) ? location.state : '(None)',
                                    dba_name: (location.dba.name) ? location.dba.name : '(None)',
                                    zip_code: (location.zipcode) ? location.zipcode : '(None)',
                                    payments: (payment) ? (toCurrency(payment / 100)) : emptyCurrency,
                                    credit: (credit) ? (toCurrency(credit / 100)) : emptyCurrency,
                                    net: (netTotal) ? (toCurrency(netTotal / 100)) : emptyCurrency
                                };
                                summaryList.push(locationRow);
                            }

                        }
                    } else {
                        // No results
                    }
                } else {
                    // Structure does not conform
                }
                // Finalize table data
                this.setState({
                    summaryList: summaryList,
                    summaryTableData: summaryList,
                    summaryComplete: true,
                });
                this.props.onSummaryComplete(true);
            }

            if (locationId) {
                if (data.aggregations.by_sale_seq && data.aggregations.by_sale_seq.buckets) {
                    let results = data.aggregations.by_sale_seq.buckets;
                    let totalResults = results.length;
                    if (totalResults) {
                        for (let j = 0; j < totalResults; j++) {
                            let detailResult = results[j];
                            let details = detailResult.column_name.hits.hits[0]._source.rxtxMsg.payload;
                            let sDate = (details.payments && details.payments[0].payment_date) ? details.payments[0].payment_date : 'No Date';
                            let sDateString = (details.payments && details.payments[0].payment_date) ? convertDate(details.payments[0].payment_date, 'display', true, true, this.state.offsetHour, true) : 'No Date';
                            let sDate24HString = (details.payments && details.payments[0].payment_date) ? convertDateTo24HourString(details.payments[0].payment_date, true) : 'No Date';

                            const detailRow = {
                                seq: (details.seq) ? details.seq : '----',
                                sale_date: sDate,
                                sale_date_string: sDateString,
                                sale_date_24h_string: sDate24HString,
                                initial_additional_taxes: (details.initial_additional_taxes) ? (toCurrency(details.initial_additional_taxes / 100)) : emptyCurrency,
                                initial_net: (details.initial_net) ? (toCurrency(details.initial_net / 100)) : emptyCurrency,
                                initial_tax: (details.initial_tax) ? (toCurrency(details.initial_tax / 100)) : emptyCurrency,
                                initial_total: (details.initial_total) ? (toCurrency(details.initial_total / 100)) : emptyCurrency,
                                register: (details.client.register) ? details.client.register : '----',
                                user: (details.client.user) ? details.client.user : '----',
                                mode: (details.mode) ? details.mode : '----',
                                version: (details.client.version) ? details.client.version : '----',
                            };
                            detailList.push(detailRow);
                        }
                    }
                }
                this.setState({
                    detailList: detailList,
                    detailLoading: false,
                    detailTableData: detailList,
                    detailComplete: true,
                });
            }
        } else {
            this.setState({
                detailLoading: false,
                summaryList: summaryList,
                summaryTableData: summaryList,
                summaryComplete: true,
            }, () => {
                this.props.onSummaryComplete(true);
            });
        }
    }

    processTransactionData(type, data) {
        const emptyCurrency = toCurrency(0);
        let paymentsList = [];
        let creditsList = [];
        let productList = [];
        let productReturnedList = [];
        let discountsList = [];
        let divisor = 100;

        let details = data.hits.hits[0]._source.rxtxMsg.payload;
        let tDateString = (details.transaction_date) ? convertDate(details.transaction_date, 'display', true, true, this.state.offsetHour, true) : 'No Date';

        if (details) {
            const detailRow = {
                dba_email: (details.location && details.location.dba) ? details.location.dba.email : '',
                dba_name: (details.location && details.location.dba) ? details.location.dba.name : '',
                device: (details.client.device) ? details.client.device : '----',
                initial_net: (details.initial_net) ? (toCurrency(details.initial_net / divisor)) : emptyCurrency,
                initial_tax: (details.initial_tax) ? (toCurrency(details.initial_tax / divisor)) : emptyCurrency,
                initial_additional_taxes: (details.initial_additional_taxes) ? (toCurrency(details.initial_additional_taxes / divisor)) : emptyCurrency,
                initial_total: (details.initial_total) ? (toCurrency(details.initial_total / divisor)) : emptyCurrency,
                location: (details.client.location) ? details.client.location : '----',
                location_address: (details.location.address) ? details.location.address : '',
                location_city: (details.location.city) ? details.location.city : '',
                location_telephone: (details.location.telephone) ? details.location.telephone : '',
                location_state: (details.location.state) ? details.location.state : '',
                location_tax_rate: (details.location.tax_rate) ? details.location.tax_rate.toString() : '0',
                location_zipcode: (details.location.zipcode) ? details.location.zipcode : '',
                loyalty_id: (details.loyalty_customer && details.loyalty_customer.id) ? details.loyalty_customer.id : '',
                register: (details.client.register) ? details.client.register : '----',
                user: (details.client.user) ? details.client.user : '----',
                version: (details.client.version) ? details.client.version : '----',
                mode: (details.mode) ? details.mode : '----',
                seq: (details.seq) ? details.seq : '----',
                transaction_date_string: tDateString,
            };

            if (details.payments) {
                let totalPayments = details.payments.length;
                for (let i = 0; i < totalPayments; i++) {
                    let paymentResult = details.payments[i];
                    const paymentRow = {
                        seq: (paymentResult.seq) ? paymentResult.seq : '----',
                        type: (paymentResult.type) ? paymentResult.type : '----',
                        card_type: (paymentResult.card_type) ? paymentResult.card_type : '----',
                        card_last4: (paymentResult.card_last4) ? paymentResult.card_last4 : '----',
                        card_data_source: (paymentResult.card_data_source) ? paymentResult.card_data_source : '----',
                        pn_ref: (paymentResult.pn_ref) ? paymentResult.pn_ref : '----',
                        authcode: (paymentResult.authcode) ? paymentResult.authcode : '----',
                        mode: (paymentResult.mode) ? paymentResult.mode : '----',
                        subtotal: (paymentResult.subtotal) ? (toCurrency(paymentResult.subtotal / divisor)) : emptyCurrency,
                        additional_taxes: (paymentResult.additional_taxes) ? paymentResult.additional_taxes : null,
                        additional_tax_total: (paymentResult.additional_tax_total) ? (toCurrency(paymentResult.additional_tax_total / divisor)) : emptyCurrency,
                        tax: (paymentResult.tax) ? (toCurrency(paymentResult.tax / divisor)) : emptyCurrency,
                        currency: (paymentResult.currency)
                            ? (paymentResult.type === 'loyalty_points')
                                ? (toCurrency(paymentResult.currency / 1000))
                                : (toCurrency(paymentResult.currency / divisor))
                            : emptyCurrency
                    };
                    paymentsList.push(paymentRow);
                }
                // Order payments: Loyalty Points, Cash, Credit
                const paymentSort = ['loyalty_points', 'cash', 'out_of_scope'];
                let paymentObj = {};
                paymentSort.forEach(function (a, i) {
                    paymentObj[a] = i + 1;
                });
                paymentsList.sort(function (a, b) {
                    return (paymentObj[a.type] || Infinity) - (paymentObj[b.type] || Infinity);
                });
                this.setState({
                    paymentsList: paymentsList
                });
            }

            if (details.credits) {
                let totalCredits = details.credits.length;
                for (let i = 0; i < totalCredits; i++) {
                    let creditResult = details.credits[i];
                    let tDateString = (creditResult.credit_date) ? convertDate(creditResult.credit_date, 'display', true, true, this.state.offsetHour, true) : 'No Date';
                    const creditRow = {
                        seq: (creditResult.seq) ? creditResult.seq : '----',
                        payment_seq: (creditResult.payment_seq) ? creditResult.payment_seq : '----',
                        type: (creditResult.type) ? creditResult.type : '----',
                        credit_date: tDateString,
                        subtotal: (creditResult.subtotal) ? (toCurrency(creditResult.subtotal / divisor)) : emptyCurrency,
                        additional_taxes: (creditResult.additional_taxes) ? creditResult.additional_taxes : null,
                        additional_tax_total: (creditResult.additional_tax_total) ? (toCurrency(creditResult.additional_tax_total / divisor)) : emptyCurrency,
                        tax: (creditResult.tax) ? (toCurrency(creditResult.tax / divisor)) : emptyCurrency,
                        currency: (creditResult.currency) ? (toCurrency(creditResult.currency / divisor)) : emptyCurrency,
                        card_type: (creditResult.card_type) ? creditResult.card_type : '----',
                        card_last4: (creditResult.card_last4) ? creditResult.card_last4 : '----',
                        card_data_source: (creditResult.card_data_source) ? creditResult.card_data_source : '----',
                        pn_ref: (creditResult.pn_ref) ? creditResult.pn_ref : '----',
                        authcode: (creditResult.authcode) ? creditResult.authcode : '----',
                        mode: (creditResult.mode) ? creditResult.mode : '----',
                        status: (creditResult.status) ? "True" : '----'

                    };
                    creditsList.push(creditRow);
                }
                this.setState({
                    creditsList: creditsList
                });
            }

            let discountTotal = 0;
            if (details.discounts) {
                let totalDiscounts = details.discounts.length;
                for (let i = 0; i < totalDiscounts; i++) {
                    let discountResult = details.discounts[i];
                    const discountRow = {
                        code: (discountResult.code) ? discountResult.code : '----',
                        name: (discountResult.name) ? discountResult.name : '----',
                        discount_each: (discountResult.discount_each) ? (toCurrency(discountResult.discount_each / divisor)) : emptyCurrency
                    };
                    discountsList.push(discountRow);
                    discountTotal += (discountResult.discount_each) ? parseInt(discountResult.discount_each, 10) : 0;
                }
                this.setState({
                    discountsList: discountsList
                });
            }

            if (details.product_units) {
                let totalProducts = details.product_units.length;
                for (let i = 0; i < totalProducts; i++) {
                    let productResult = details.product_units[i];
                    // Use Display Price until calculations are adjusted
                    let productQty = (productResult.qty) ? productResult.qty : 0;
                    let displayPrice = (productResult.display_price) ? productResult.display_price : 0;
                    let displaySubtotal = (displayPrice * productQty);
                    const productRow = {
                        product_unit_id: (productResult.product_unit_id) ? productResult.product_unit_id : '----',
                        product_name: (productResult.product_name) ? productResult.product_name : '----',
                        product_unit_type: (productResult.product_unit_type) ? productResult.product_unit_type : '----',
                        qty: (productResult.qty) ? productResult.qty : '----',
                        price_each: (productResult.price_each) ? (toCurrency(productResult.price_each / divisor)) : emptyCurrency,
                        price_each_display: (displayPrice) ? toCurrency(displayPrice) : emptyCurrency,
                        price_subtotal: (productResult.price_subtotal) ? (toCurrency(productResult.price_subtotal / divisor)) : emptyCurrency,
                        price_subtotal_display: (displaySubtotal) ? toCurrency(displaySubtotal) : emptyCurrency,
                        price_total: (productResult.price_total) ? (toCurrency(productResult.price_total / divisor)) : emptyCurrency,
                        price_tax_each: (productResult.price_tax_each) ? (toCurrency(productResult.price_tax_each / divisor)) : emptyCurrency,
                        price_tax_total: (productResult.price_tax_total) ? (toCurrency(productResult.price_tax_total / divisor)) : emptyCurrency
                    };
                    productList.push(productRow);
                }
                this.setState({
                    productList: productList
                });
            }

            if (details.product_units_returned) {
                let totalProductsReturned = details.product_units_returned.length;
                for (let i = 0; i < totalProductsReturned; i++) {
                    let productReturnedResult = details.product_units_returned[i];
                    const productReturnedRow = {
                        product_unit_id: (productReturnedResult.product_unit_id) ? productReturnedResult.product_unit_id : '----',
                        product_name: (productReturnedResult.product_name) ? productReturnedResult.product_name : '----',
                        product_unit_type: (productReturnedResult.product_unit_type) ? productReturnedResult.product_unit_type : '----',
                        qty: (productReturnedResult.qty) ? productReturnedResult.qty : '----',
                    };
                    productReturnedList.push(productReturnedRow);
                }
                this.setState({
                    productReturnedList: productReturnedList
                });
            }

            detailRow.discount_total = (discountTotal) ? (toCurrency(discountTotal / divisor)) : emptyCurrency;
            detailRow.discount_total_string = detailRow.discount_total;
            if (discountTotal < 0) {
                const discountAmount = discountTotal.toString().substr(1);
                detailRow.discount_total_string = '-$' + toCurrency(parseInt(discountAmount, 10) / 100);
            }
            this.setState({
                transactionData: detailRow
            });
        }
    }

    /* Table Building ------------------------------ */
    handleLocationSelect(event, row) {
        const location = {
            id: row.location_id,
            name: row.location_name,
            selected: true,
            city: row.city,
            state: row.state,
            dba_name: row.dba_name
        };
        this.setState({
            allowLocationSelection: true,
            detailLocation: location,
            detailLoading: true
        }, () => {
            this.fetchData(location.id);
        });
    }

    /**
     * Update selected table data based on sort
     *
     * @param event
     * @param tableName
     * @param table
     */
    handleSortOrFilterChange(event, tableName, table) {
        const tableData = getTableState(table);
        if (tableData && tableData.length > 0) {
            if (tableName === 'detail') {
                this.setState({
                    detailTableData: tableData
                });
            } else {
                this.setState({
                    summaryTableData: tableData
                });
            }
        }
    }

    /**
     * Open/Close Dialog
     * @param e
     * @param seq
     * @param type
     */
    handleOpen = (e, seq, type) => {
        this.setState({open: true},
            () => {
                this.fetchTransactionData(seq, type);
            });
    };

    handleClose = () => {
        this.setState({
            open: false,
            paymentsList: [],
            creditsList: [],
            productList: [],
            productReturnedList: [],
            discountsList: []
        });
    };

    /**
     * Handle event to retrieve current state of table data
     *
     * @param event
     */
    handleExportClick = (event) => {
        // CSVLink handles generation and export of CSV
    };

    /* Table Display ------------------------------ */

    render() {

        const {
            includeReceipt,
            system
        } = this.props;

        const {
            allowLocationSelection,
            detailComplete,
            detailLoading,
            detailTableData,
            detailTableHeaders,
            detailLocation,
            detailList,
            summaryList,
            summaryTableData,
            summaryTableHeaders,
            summaryComplete,
            transactionData,
            paymentsList,
            creditsList,
            productList,
            productReturnedList,
            discountsList,
            open
        } = this.state;

        const defaultSort = setDefaultTableSort('location_id');
        const defaultDetailSort = setDefaultTableSort({field: 'sale_date', order: 'DESC'});
        const iconStyle = (allowLocationSelection) ? tableStyles.tableIconButton : tableStyles.disabledTableIconButton;

        // ReactTable headers and columns for Table
        const tableColumns = [
            {
                Header: '',
                columns: [
                    {
                        Header: 'Location ID',
                        accessor: 'location_id',
                        Cell: row => (
                            <span>
                                <span>{row.row.location_id}</span>
                                <DetailIcon
                                    onClick={(e) => this.handleLocationSelect(e, row.row)}
                                    style={iconStyle}
                                />
                            </span>
                        ),
                        style: tableStyles.right
                    },
                    {
                        Header: 'Location Name',
                        accessor: 'location_name',
                    },
                    {
                        Header: 'City',
                        accessor: 'city',
                    },
                    {
                        Header: 'State',
                        accessor: 'state',
                    },
                    {
                        Header: 'DBA',
                        accessor: 'dba_name',
                    },
                    {
                        Header: 'Payments',
                        accessor: 'payments',
                        sortMethod: (a, b) => sortCurrency(a, b),
                        filterable: false,
                        Footer: (
                            <span style={tableStyles.footer}>
                                {addCommas(addFromColumn(summaryTableData, 'payments').toFixed(2))}
                            </span>
                        ),
                        style: {...tableStyles.tdMax, ...tableStyles.right}
                    },
                    {
                        Header: 'Credits',
                        accessor: 'credit',
                        sortMethod: (a, b) => sortCurrency(a, b),
                        filterable: false,
                        Footer: (
                            <span style={tableStyles.footer}>
                                {addCommas(addFromColumn(summaryTableData, 'credit').toFixed(2))}
                            </span>
                        ),
                        style: {...tableStyles.tdMax, ...tableStyles.right}
                    },
                    {
                        Header: 'Net Total',
                        accessor: 'net',
                        sortMethod: (a, b) => sortCurrency(a, b),
                        filterable: false,
                        Footer: (
                            <span style={tableStyles.footer}>
                                {addCommas(addFromColumn(summaryTableData, 'net').toFixed(2))}
                            </span>
                        ),
                        style: {...tableStyles.tdMax, ...tableStyles.right}
                    }
                ]
            }
        ];
        const tableDetailsColumns = [
            {
                Header: '',
                columns: [
                    {
                        Header: 'Sale Id',
                        accessor: 'seq',
                        style: tableStyles.wrapText,
                        Cell: props => (
                            <a style={tableStyles.linkDefault}
                               onClick={(e) => this.handleOpen(e, props.row.seq, props.row.transaction)}
                            >
                                {props.row.seq}
                            </a>
                        )
                    },
                    {
                        Header: 'Processed',
                        accessor: 'sale_date',
                        Cell: props => (
                            <span>{props.row.sale_date_string}</span>
                        )
                    },
                    {
                        Header: '',
                        accessor: 'sale_date_string',
                        style: tableStyles.hide,
                        headerStyle: tableStyles.hide
                    },
                    {
                        Header: 'Subtotal',
                        accessor: 'initial_net',
                        sortMethod: (a, b) => sortCurrency(a, b),
                        filterable: false,
                        style: tableStyles.right
                    },
                    {
                        Header: 'Tax',
                        accessor: 'initial_tax',
                        sortMethod: (a, b) => sortCurrency(a, b),
                        filterable: false,
                        style: tableStyles.right
                    },
                    {
                        Header: () => (<span>Additional<br/>Tax</span>),
                        accessor: 'initial_additional_taxes',
                        sortMethod: (a, b) => sortCurrency(a, b),
                        filterable: false,
                        style: tableStyles.right
                    },
                    {
                        Header: 'Total',
                        accessor: 'initial_total',
                        sortMethod: (a, b) => sortCurrency(a, b),
                        filterable: false,
                        style: tableStyles.right
                    },
                    {
                        Header: 'Register',
                        accessor: 'register'
                    },
                    {
                        Header: 'User',
                        accessor: 'user'
                    },
                    {
                        Header: 'Mode',
                        accessor: 'mode'
                    },
                    {
                        Header: 'Version',
                        accessor: 'version'
                    },
                ]
            }
        ];

        // Assemble Location Detail information
        const locationCity = (detailLocation && detailLocation.city) ? detailLocation.city + ', ' : '';
        const locationState = (detailLocation && detailLocation.state) ? detailLocation.state + '.' : '';

        // Dialog Actions
        const dialogActions = [
            <FlatButton
                label="Close"
                primary={true}
                onClick={this.handleClose}
            />
        ];

        return (
            <div>
                {summaryComplete &&
                    <div className="reports-table-container">

                        {/* Table Header: Recent Transactions */}
                        <div className="reports-table-title table-table-header">
                            <div className="btn-report-container report-header-button-container">
                                {/* Export Recent Transactions */}
                                <FlatButton
                                    primary
                                    type="button"
                                    label=""
                                    style={commonStyles.headerButton}
                                >
                                    <ExportIcon style={commonStyles.headerButtonIcon}/>
                                    <CSVLink
                                        data={summaryTableData}
                                        headers={summaryTableHeaders}
                                        filename={'Recent-Transactions-' + system + '.csv'}
                                        onClick={(e) => this.handleExportClick(e)}
                                        target="_blank"
                                        style={commonStyles.headerButtonLink}
                                    >
                                        Export Recent Transactions Report
                                    </CSVLink>
                                </FlatButton>
                            </div>
                            <h3 className="table-table-header-title">
                                <span>Recent Transactions</span>
                            </h3>
                        </div>

                        {/* Table: Recent Transactions */}
                        <ReactTable
                            key="table-recent-transactions"
                            data={summaryList}
                            columns={tableColumns}
                            defaultPageSize={5}
                            showPagination={true}
                            defaultSorted={defaultSort}
                            filterable
                            defaultFilterMethod={filterCaseInsensitive}
                            onSortedChange={(e) => this.handleSortOrFilterChange(e, 'summary', this.reactTable)}
                            onFilteredChange={(e) => this.handleSortOrFilterChange(e, 'summary', this.reactTable)}
                            ref={(r) => this.reactTable = r}
                            resizable={false}
                            minRows={1}
                            className="-striped"
                        />
                        <div ref={(ref) => this.detailPosition = ref}>
                        </div>
                    </div>
                }

                {detailLoading && !detailComplete &&
                    <div className="loader-container">
                        <h4>Generating Report...</h4>
                        <CircularProgress
                            size={24}
                            thickness={2}
                            className="loader"
                        />
                    </div>
                }

                {detailComplete &&
                    <div className="reports-table-container">

                        {/* Table Header: Detail Sales Logs (Selected Location) */}
                        <div className="reports-table-title table-table-header">
                            <div className="btn-report-container report-header-button-container">
                                {/* Export Detail Recent Transaction Logs (Selected Location) */}
                                <FlatButton
                                    primary
                                    type="button"
                                    label=""
                                    style={commonStyles.headerButton}
                                >
                                    <ExportIcon style={commonStyles.headerButtonIcon}/>
                                    <CSVLink
                                        data={detailTableData}
                                        headers={detailTableHeaders}
                                        filename={'Recent-Transaction-Logs-Report-' + system + '-' + detailLocation.id + '.csv'}
                                        onClick={(e) => this.handleExportClick(e)}
                                        target="_blank"
                                        style={commonStyles.headerButtonLink}
                                    >
                                        Export Recent Sales Logs Report {detailLocation.id}
                                    </CSVLink>
                                </FlatButton>
                            </div>
                            <h3 className="table-table-header-title">
                                <span>Recent Sales Logs by Location {detailLocation.id}</span>
                            </h3>
                            <div style={commonStyles.tableDetail}>
                                Location Name: {detailLocation.name}<br/>
                                DBA: {detailLocation.dba_name}<br/>
                                {locationCity}{locationState}
                            </div>
                        </div>

                        {/* Table: Recent Transaction Logs (Selected Location) */}
                        <ReactTable
                            key="table-recent-transaction-log-detail"
                            data={detailList}
                            columns={tableDetailsColumns}
                            defaultPageSize={10}
                            showPagination={true}
                            defaultSorted={defaultDetailSort}
                            filterable
                            defaultFilterMethod={filterCaseInsensitive}
                            onSortedChange={(e) => this.handleSortOrFilterChange(e, 'detail', this.detailReactTable)}
                            onFilteredChange={(e) => this.handleSortOrFilterChange(e, 'detail', this.detailReactTable)}
                            ref={(r) => this.detailReactTable = r}
                            resizable={false}
                            minRows={1}
                            className="-striped overflow no-print"
                        />
                        <ReactTable
                            key="table-recent-transaction-log-detail-print"
                            data={detailTableData}
                            columns={tableDetailsColumns}
                            defaultPageSize={10000}
                            showPagination={false}
                            defaultSorted={defaultDetailSort}
                            defaultSortDesc={true}
                            filterable
                            resizable={false}
                            minRows={1}
                            className="print-only"
                        />

                        <Dialog
                            modal={false}
                            open={open}
                            onRequestClose={this.handleClose}
                            contentClassName={"dialogContentTable"}
                            actionsContainerClassName={"dialogPrintOnly"}
                            autoScrollBodyContent={true}
                            actions={dialogActions}
                        >
                            <div style={tableStyles.flexContainerRight}>
                                {includeReceipt &&
                                    <ReactToPrint
                                        trigger={() =>
                                            <RaisedButton
                                                primary
                                                type="button"
                                                label="Print Receipt"
                                                style={tableStyles.printBtn}
                                            />
                                        }
                                        content={() => this.printReceiptRef}
                                    />
                                }
                                <ReactToPrint
                                    trigger={() =>
                                        <RaisedButton
                                            primary
                                            type="button"
                                            label="Print Report"
                                            style={tableStyles.printBtn}/>
                                    }
                                    content={() => this.printReportRef}
                                />
                            </div>
                            {!transactionData &&
                                <div className="loader-container">
                                    <h4>Generating Sales Log...</h4>
                                    <CircularProgress
                                        size={24}
                                        thickness={2}
                                        className="loader"
                                    />
                                </div>
                            }
                            {transactionData &&
                                <Fragment>
                                    <DialogSaleSeq
                                        ref={e => (this.printReportRef = e)}
                                        data={transactionData}
                                        payments={paymentsList}
                                        credits={creditsList}
                                        products={productList}
                                        returns={productReturnedList}
                                        discounts={discountsList}
                                        handleSortOrFilterChange={this.handleSortOrFilterChange}
                                    />
                                    {includeReceipt &&
                                        <Fragment>
                                            <DialogSaleReceipt
                                                ref={e => (this.printReceiptRef = e)}
                                                data={transactionData}
                                                payments={paymentsList}
                                                products={productList}
                                                discounts={discountsList}
                                            />
                                            <div style={tableStyles.center}>
                                                <ReactToPrint
                                                    trigger={() =>
                                                        <RaisedButton
                                                            primary
                                                            type="button"
                                                            label="Print Receipt"
                                                            style={tableStyles.printBtnCenter}/>
                                                    }
                                                    content={() => this.printReceiptRef}
                                                />
                                            </div>
                                        </Fragment>
                                    }
                                </Fragment>
                            }
                        </Dialog>
                    </div>
                }
            </div>
        )
    }
}

RecentTransactionsReport.propTypes = {
    addField: PropTypes.bool,
    daysBack: PropTypes.string,
    includeReceipt: PropTypes.bool,
    onDisplayMessage: PropTypes.func,
    onError: PropTypes.func,
    onSummaryComplete: PropTypes.func,
    system: PropTypes.string,
};

RecentTransactionsReport.defaultProps = {
    addField: true,
    daysBack: '30',
    includeReceipt: true
};

const enhance = compose(
    connect()
);

export default enhance(RecentTransactionsReport);



// WEBPACK FOOTER //
// ./src/Fuse/components/RecentTransactionsReport.js